import * as React from 'react'

import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import SelectField from 'modules/core/components/SelectField'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import allowances from 'modules/timeoff/store/collections/allowances'
import { BulkAssignEmployeesDto } from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import type Allowance from 'modules/timeoff/store/models/Allowance'

interface Props {
  policyId: number | undefined
  currentAllowances: Allowance[]
  setBulkAssignEmployeeDto: (value: BulkAssignEmployeesDto) => void
  bulkAssignEmployeeDto: BulkAssignEmployeesDto
}

const AllowanceMapper = ({
  policyId,
  currentAllowances,
  setBulkAssignEmployeeDto,
  bulkAssignEmployeeDto,
}: Props) => {
  const { navigation } = useNavigation()
  const possibleAllowances = allowances.filter((a) => a.attributes.timeoff_policy_id === policyId)

  const toSelectOption = (allowance: Allowance) => ({
    label: allowance.attributes.name,
    value: allowance.attributes.id,
  })
  const hourlyAllowancesOptions: { label: string; value: number }[] = []
  const dailyAllowancesOptions: { label: string; value: number }[] = []
  possibleAllowances.forEach((allowance) => {
    if (allowance.attributes.timeoff_policy_id === policyId) {
      if (allowance.isHourly) {
        hourlyAllowancesOptions.push(toSelectOption(allowance))
      } else {
        dailyAllowancesOptions.push(toSelectOption(allowance))
      }
    }
  })

  const ignoreOption = { label: 'Ignore', value: -1 }
  if (dailyAllowancesOptions.length !== 0) {
    dailyAllowancesOptions.unshift(ignoreOption)
  }

  if (hourlyAllowancesOptions.length !== 0) {
    hourlyAllowancesOptions.unshift(ignoreOption)
  }

  const setNewAllowance = (chosenAllowanceId: string, oldAllowanceId: number) => {
    const mappedAllowances = [...bulkAssignEmployeeDto.mappings]
    const foundItem = mappedAllowances.find((item) => {
      return item.originalAllowanceId == oldAllowanceId
    })
    foundItem!.newAllowanceId = chosenAllowanceId === '' ? 0 : parseInt(chosenAllowanceId, 10)

    setBulkAssignEmployeeDto({
      employeeIds: bulkAssignEmployeeDto.employeeIds,
      mappings: mappedAllowances,
      policyId: bulkAssignEmployeeDto.policyId,
    })
  }

  let allowanceTypeMissing = false
  const findAllowanceType = (allowance: Allowance) => {
    if (allowance.isHourly) {
      if (hourlyAllowancesOptions.length === 0) {
        allowanceTypeMissing = true
        return []
      }
      return hourlyAllowancesOptions
    }
    if (allowance.isDays) {
      if (dailyAllowancesOptions.length === 0) {
        allowanceTypeMissing = true
        return []
      }
      return dailyAllowancesOptions
    }
  }

  return (
    <>
      <Box paddingBottom="s16">
        <Text size="200" weight="normal" color="grey800">
          {i18n.t('settings.timeoff_policies.manual_policy_mapper.multiple_allowances')}
        </Text>
      </Box>
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceEvenly"
        gap="s8"
        paddingTop="s24"
      >
        <Box minWidth="s240" paddingLeft="s4">
          <Text size="300" weight="medium" color="darker800">
            {i18n.t('settings.timeoff_policies.manual_policy_mapper.previous_allowance')}
          </Text>
        </Box>
        <Box></Box>
        <Box width="s200">
          <Text size="300" weight="medium" color="darker800">
            {i18n.t('settings.timeoff_policies.manual_policy_mapper.transfer_to')}
          </Text>
        </Box>
      </Box>
      {currentAllowances.map((allowance) => {
        return (
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="spaceEvenly"
            gap="s8"
            paddingTop="s24"
            key={allowance.id}
          >
            <Box minWidth="s200">
              <Text size="300" weight="normal">
                {allowance.attributes.name}
              </Text>
            </Box>
            <Box alignItems="center">
              <Icon.Medium icon="arrow-right" />
            </Box>
            <Box width="s200">
              {findAllowanceType(allowance)!.length === 0 ? (
                <Box paddingLeft="s6">
                  <Text size="300" weight="normal" color="radicalRed800">
                    {allowance.isHourly
                      ? i18n.t(
                          'settings.timeoff_policies.manual_policy_mapper.hourly_allowance_missing'
                        )
                      : i18n.t(
                          'settings.timeoff_policies.manual_policy_mapper.daily_allowance_missing'
                        )}
                  </Text>
                </Box>
              ) : (
                <SelectField
                  options={findAllowanceType(allowance) ?? []}
                  defaultOption={{
                    value: '',
                    label: i18n.t('common.select'),
                  }}
                  key={allowance.id}
                  showOriginalLabelText
                  mandatory
                  onChange={(value) => setNewAllowance(value, allowance.id)}
                />
              )}
            </Box>
          </Box>
        )
      })}
      {allowanceTypeMissing && !!policyId && (
        <Box paddingTop="s24">
          <Callout
            type="info"
            title={i18n.t('settings.timeoff_policies.manual_policy_mapper.title')}
            description={i18n.t('settings.timeoff_policies.manual_policy_mapper.description')}
            action={
              <Button.Router
                to={navigation.settings.timeoffPolicies.id.details.addAllowance.fullPath({
                  id: policyId,
                })}
                type="secondary"
              >
                {i18n.t('settings.timeoff_policies.manual_policy_mapper.link_text')}
              </Button.Router>
            }
            onRemove={() => {
              return null
            }}
          />
        </Box>
      )}
    </>
  )
}

export default observer(AllowanceMapper)
